import { createLowlight } from 'lowlight';
import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import latex from 'highlight.js/lib/languages/latex';
import markdown from 'highlight.js/lib/languages/markdown';
import plaintext from 'highlight.js/lib/languages/plaintext';
import python from 'highlight.js/lib/languages/python';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import katex from 'katex';
import { sanitizeHtml } from './sanitize';

/**
 * Code highlighting and typeset math for notes.
 *
 * Stored note HTML stays plain: a code block is <pre><code class="language-x">
 * and an equation is an empty <span data-type="inline-math" data-latex="…">
 * (or a <div data-type="block-math">). The colours and the typesetting are
 * added at display time, here for read-only views and by the editor's own
 * node views while editing, so nothing presentational ever gets saved.
 */

// Only the languages the menu offers, which is also what auto-detection
// picks between. lowlight's `common` set is three times the size of the
// rest of this feature and has no LaTeX in it anyway.
export const lowlight = createLowlight({
  bash, css, javascript, json, latex, markdown, plaintext, python, sql, typescript, xml,
});

// What the code-block language menu offers. Markdown and LaTeX lead because
// they are what notes are written in; the rest cover the commands and
// snippets people already keep. An empty value means "work it out".
export const CODE_LANGUAGES: { value: string; label: string }[] = [
  { value: '', label: 'Auto' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'latex', label: 'LaTeX' },
  { value: 'bash', label: 'Bash' },
  { value: 'python', label: 'Python' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'HTML/XML' },
  { value: 'css', label: 'CSS' },
  { value: 'sql', label: 'SQL' },
  { value: 'plaintext', label: 'Plain text' },
];

export const KATEX_OPTIONS = { throwOnError: false, strict: 'ignore' as const };

interface HastText { type: 'text'; value: string }
interface HastElement {
  type: 'element';
  properties?: { className?: string[] | string };
  children: HastNode[];
}
type HastNode = HastText | HastElement | { type: string; children?: HastNode[] };

// lowlight returns a hast tree; build it as DOM nodes rather than an HTML
// string so the code text only ever reaches the page as text.
const appendHast = (parent: Node, nodes: HastNode[]) => {
  nodes.forEach((node) => {
    if (node.type === 'text') {
      parent.appendChild(document.createTextNode((node as HastText).value));
    } else if (node.type === 'element') {
      const el = node as HastElement;
      const span = document.createElement('span');
      const cls = el.properties?.className;
      if (cls) span.className = Array.isArray(cls) ? cls.join(' ') : cls;
      appendHast(span, el.children);
      parent.appendChild(span);
    }
  });
};

const languageOf = (code: Element): string | null => {
  const match = Array.from(code.classList).find((c) => c.startsWith('language-'));
  return match ? match.slice('language-'.length) : null;
};

const highlightCode = (code: Element) => {
  const text = code.textContent || '';
  if (!text.trim()) return;
  const language = languageOf(code);
  if (language === 'plaintext') return;
  const tree = language && lowlight.registered(language)
    ? lowlight.highlight(language, text)
    : lowlight.highlightAuto(text);
  // Auto-detection that recognises nothing returns an empty tree, not the
  // text unhighlighted; leave the block exactly as it was.
  if (!tree.children.length) return;
  code.textContent = '';
  code.classList.add('hljs');
  appendHast(code, tree.children as HastNode[]);
};

const renderMath = (el: HTMLElement, displayMode: boolean) => {
  const source = el.getAttribute('data-latex') || '';
  try {
    katex.render(source, el, { ...KATEX_OPTIONS, displayMode });
  } catch {
    el.textContent = source;
  }
};

/**
 * Colour the code blocks and typeset the equations in already-sanitised note
 * HTML. The input must have been through sanitizeHtml: this only adds
 * highlighter spans and KaTeX markup to it, and never reads markup out of a
 * code block or an equation's source.
 */
export const enhanceNoteHtml = (sanitizedHtml: string): string => {
  if (!sanitizedHtml) return '';
  const hasCode = sanitizedHtml.includes('<pre');
  const hasMath = sanitizedHtml.includes('-math');
  if (!hasCode && !hasMath) return sanitizedHtml;

  // A <template>'s content is inert: nothing in it loads or runs.
  const template = document.createElement('template');
  template.innerHTML = sanitizedHtml;
  const root = template.content;

  if (hasCode) root.querySelectorAll('pre > code').forEach(highlightCode);
  if (hasMath) {
    root.querySelectorAll<HTMLElement>('[data-type="inline-math"]').forEach((el) => renderMath(el, false));
    root.querySelectorAll<HTMLElement>('[data-type="block-math"]').forEach((el) => renderMath(el, true));
  }
  return template.innerHTML;
};

/** Sanitise note HTML and dress it for reading, in that order. */
export const renderNoteHtml = (html: string): string => enhanceNoteHtml(sanitizeHtml(html));

/**
 * Equations written back out as their source, for anything that shows a note
 * as text (the clipboard, plain previews). An equation node has no text of
 * its own, so a tag strip would silently drop it.
 */
export const mathSourceText = (el: Element): string | null => {
  const type = el.getAttribute('data-type');
  if (type !== 'inline-math' && type !== 'block-math') return null;
  const source = el.getAttribute('data-latex') || '';
  return type === 'block-math' ? `\n$$${source}$$\n` : `$${source}$`;
};
